/* eslint-disable react-hooks/exhaustive-deps */
import { GoogleMap } from '@react-google-maps/api'
import { useSearchParams } from 'react-router'
import { useCallback, useEffect, useRef, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../button/button'
import LoginModal from '../login-modal/login-modal'
import MapMarker from './map-marker'
import MapWindow from './map-window'
import { setUserCoordinates } from '~/utils/location/location'
import { debounce } from '~/utils/misc'
import type { EventIndex } from '~/types/eventIndex'
import type { UserLocation } from '~/types/location'

export default function Map({
	events,
	userLocation,
	isLoaded,
}: {
	events: EventIndex[]
	userLocation: UserLocation | null
	isLoaded: boolean
}) {
	const [searchParams, setSearchParams] = useSearchParams()
	const categories = searchParams.get('categories') ?? undefined
	const country = searchParams.get('country') ?? undefined
	const city = searchParams.get('city') ?? undefined
	const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false)
	const [showLoginModal, setShowLoginModal] = useState(false)
	const [map, setMap] = useState<google.maps.Map | null>(null)
	const mapRef = useRef<google.maps.Map | null>(null)
	const [selectedMarker, setSelectedMarker] = useState<null | EventIndex[]>(
		null,
	)
	const { t } = useTranslation()
	const [markers, setMarkers] = useState<JSX.Element[]>([])

	useEffect(() => {
		if (
			typeof window.google === 'object' &&
			typeof window.google.maps === 'object'
		) {
			setIsGoogleMapsLoaded(true)
		} else {
			const checkGoogleMaps = setInterval(() => {
				if (
					typeof window.google === 'object' &&
					typeof window.google.maps === 'object'
				) {
					clearInterval(checkGoogleMaps)
					setIsGoogleMapsLoaded(true)
				}
			}, 100)
		}
	}, [])

	const onLoad = useCallback(async (map: google.maps.Map) => {
		setMap(map)
		mapRef.current = map
		if (
			userLocation &&
			userLocation.neLat &&
			userLocation.neLng &&
			userLocation.swLat &&
			userLocation.swLng
		) {
			const bounds = new google.maps.LatLngBounds(
				new google.maps.LatLng(userLocation.swLat, userLocation.swLng),
				new google.maps.LatLng(userLocation.neLat, userLocation.neLng),
			)
			map.fitBounds(bounds)
		} else if (events.length > 0) {
			const bounds = new google.maps.LatLngBounds()
			events.forEach(({ latitude, longitude }) =>
				bounds.extend({ lat: latitude, lng: longitude }),
			)
			map.fitBounds(bounds)
		} else if (
			userLocation?.havePosition &&
			userLocation.latitude &&
			userLocation.longitude
		) {
			map.setZoom(13)
			map.panTo({ lat: userLocation.latitude, lng: userLocation.longitude })
		} else {
			map.fitBounds({
				north: 69.06,
				south: 55.34,
				west: 10.59,
				east: 24.19,
			}) // Sweden
		}
	}, [])

	const onUnmount = useCallback(() => {
		setMap(null)
		mapRef.current = null
	}, [])

	const setCurrentUserLocation = () => {
		if (navigator.geolocation) {
			navigator.geolocation.getCurrentPosition(
				(position: GeolocationPosition) => {
					const { latitude, longitude } = position.coords
					map?.setZoom(13)
					map?.panTo({ lat: latitude, lng: longitude })
				},
			)
			setUserCoordinates()
		}
	}

	const mapChange = () => {
		if (!mapRef.current) return
		const bounds = map?.getBounds()
		const boundsSw = bounds?.getSouthWest()
		const boundsNe = bounds?.getNorthEast()
		const swLat = boundsSw?.lat()
		const swLng = boundsSw?.lng()
		const neLat = boundsNe?.lat()
		const neLng = boundsNe?.lng()
		if (isNaN(swLat!) || isNaN(swLng!) || isNaN(neLat!) || isNaN(neLng!)) return
		updateURLParams(swLat!, swLng!, neLat!, neLng!)
	}
	const toggleModal = () => setShowLoginModal(prev => !prev)
	const updateURLParams = (
		swLat: number,
		swLng: number,
		neLat: number,
		neLng: number,
	) => {
		const newSearchParams = new URLSearchParams(searchParams.toString())
		newSearchParams.set('swLat', swLat.toFixed(8))
		newSearchParams.set('swLng', swLng.toFixed(8))
		newSearchParams.set('neLat', neLat.toFixed(8))
		newSearchParams.set('neLng', neLng.toFixed(8))
		newSearchParams.delete('country')
		newSearchParams.delete('city')
		newSearchParams.delete('nearby')
		setSearchParams(newSearchParams)
	}

	useEffect(() => {
		mapChange()
	}, [categories])

	useEffect(() => {
		if (!mapRef.current) return
		const neLat = searchParams.get('neLat')
		const neLng = searchParams.get('neLng')
		const swLat = searchParams.get('swLat')
		const swLng = searchParams.get('swLng')
		if (country && city && neLat && neLng && swLat && swLng) {
			const bounds = new google.maps.LatLngBounds(
				new google.maps.LatLng(parseFloat(swLat), parseFloat(swLng)),
				new google.maps.LatLng(parseFloat(neLat), parseFloat(neLng)),
			)
			map?.fitBounds(bounds)
		}
	}, [country, city])

	useEffect(() => {
		const groupedMarkers: EventIndex[][] = []
		const returnMarkers: JSX.Element[] = []

		events.forEach((event: EventIndex) => {
			let isGrouped = false

			// Check if the marker's position matches any existing group
			for (let i = 0; i < groupedMarkers.length; i++) {
				const group = groupedMarkers[i]
				const firstMarker = group[0]

				if (
					firstMarker.latitude === event.latitude &&
					firstMarker.longitude === event.longitude
				) {
					// Add the marker to the existing group
					group.push(event)
					isGrouped = true
					break
				}
			}

			// If the marker doesn't match any existing group, create a new group
			if (!isGrouped) {
				groupedMarkers.push([event])
			}
		})

		groupedMarkers.forEach((items: EventIndex[]) => {
			returnMarkers.push(
				<MapMarker
					key={items[0].dateId}
					markers={items}
					active={
						selectedMarker
							? items[0].dateId === selectedMarker![0].dateId
							: false
					}
					onClick={setSelectedMarker}
				/>,
			)
		})

		setMarkers(returnMarkers.reverse())
	}, [events, selectedMarker])

	return (
		<>
			{showLoginModal && (
				<LoginModal
					text={t('misc_login_to_add_favorite')}
					toggleLoginModal={toggleModal}
					initialPage={0}
				/>
			)}
			<div className="map-container h-full w-full pt-[81px] md:pt-[73px]">
				{isLoaded && isGoogleMapsLoaded && (
					<GoogleMap
						onLoad={onLoad}
						onUnmount={onUnmount}
						onCenterChanged={map != null ? debounce(mapChange, 600) : () => {}}
						mapContainerClassName="map-container h-full w-full"
						options={{
							streetViewControl: false,
							mapId: ENV.GOOGLE_MAPID_LIGHT,
							mapTypeControlOptions: {
								mapTypeIds: [],
							},
							maxZoom: 18,
							minZoom: 5,
							fullscreenControl: false,
							clickableIcons: false,
							tilt: 0,
							zoomControl:
								typeof window !== 'undefined' && window.innerWidth > 768,
							zoomControlOptions: {
								position: google.maps.ControlPosition.RIGHT_TOP,
							},
							gestureHandling: 'greedy',
							restriction: {
								latLngBounds: {
									north: 85,
									south: -85,
									west: -180,
									east: 180,
								},
								strictBounds: true,
							},
						}}
						clickableIcons={false}
					>
						<>
							{markers}

							{map && selectedMarker && (
								<MapWindow
									events={selectedMarker}
									map={map}
									toggleModal={toggleModal}
									onClose={setSelectedMarker}
								/>
							)}
							<Button
								variant="white"
								size="sm"
								className="absolute right-2 top-2 z-20 mr-[1.1px]! h-[40px]! w-[40px]! rounded-sm! shadow-xl! md:top-24"
								onClick={setCurrentUserLocation}
								title={t('action_show_my_position')}
							>
								<svg
									width="20"
									height="20"
									viewBox="0 0 122.88 122.88"
									fill="#555"
								>
									<g>
										<path d="M68.23,13.49c10.44,1.49,19.79,6.36,26.91,13.48c7.29,7.29,12.23,16.93,13.58,27.68h14.17v13.58h-14.39 c-1.62,10.13-6.42,19.2-13.36,26.13c-7.11,7.11-16.47,11.99-26.91,13.48v15.04H54.65v-15.04c-10.44-1.49-19.79-6.36-26.9-13.48 c-6.94-6.94-11.74-16-13.36-26.13H0V54.65h14.16c1.35-10.75,6.29-20.39,13.58-27.68c7.11-7.11,16.46-11.99,26.9-13.48V0h13.58 V13.49L68.23,13.49z M61.44,35.41c13.95,0,25.25,11.31,25.25,25.25c0,13.95-11.31,25.25-25.25,25.25 c-13.95,0-25.25-11.31-25.25-25.25C36.19,46.72,47.49,35.41,61.44,35.41L61.44,35.41z M89,33.11c-7.05-7.05-16.8-11.42-27.56-11.42 c-10.76,0-20.51,4.36-27.56,11.42c-7.05,7.05-11.42,16.8-11.42,27.56c0,10.76,4.36,20.51,11.42,27.56 c7.05,7.05,16.8,11.42,27.56,11.42c10.76,0,20.51-4.36,27.56-11.42c7.05-7.05,11.42-16.8,11.42-27.56 C100.41,49.9,96.05,40.16,89,33.11L89,33.11z" />
									</g>
								</svg>
							</Button>
						</>
					</GoogleMap>
				)}
			</div>
		</>
	)
}
